import { useState } from "react";
import { MapPin, Mail, Globe, Camera, Music2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../lib/seo";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { useSettings } from "../lib/store";
import { postInquiry } from "../lib/api";
import { track } from "../lib/track";
import { waLink, MSG } from "../lib/whatsapp";
import { WhatsAppIcon } from "../components/icons";

const HERO_IMG =
  "https://images.unsplash.com/photo-1518235506717-e1ed3306a89b?crop=entropy&cs=srgb&fm=jpg&q=80&auto=format&fit=crop&w=1800";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", whatsapp: "", message: "" });
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim()) {
      toast.error("Mohon isi nama dan nomor WhatsApp.");
      return;
    }
    try {
      await postInquiry({ type: "contact", ...form, source: "contact-page" });
      track("request_quotation", { source: "contact_form" });
      setSent(true);
      toast.success("Pesan terkirim. Tim kami akan segera menghubungi Anda.");
    } catch {
      toast.error("Gagal mengirim pesan. Coba lagi.");
    }
  };

  const cards = [
    {
      icon: WhatsAppIcon, label: "WhatsApp", value: settings?.whatsapp_display || "+62 881-2551-877",
      href: waLink(settings, MSG.general), testid: "contact-whatsapp",
    },
    { icon: Mail, label: "Email", value: "info@beyondtravel.co.id", href: "mailto:info@beyondtravel.co.id", testid: "contact-email" },
    { icon: Globe, label: "Website", value: "beyondtravel.co.id", href: "https://beyondtravel.co.id", testid: "contact-website" },
    { icon: Camera, label: "Instagram", value: "@beyondtravel.co.id", href: "https://instagram.com/beyondtravel.co.id", testid: "contact-instagram" },
    { icon: Music2, label: "TikTok", value: "@BeyondTourTravel", href: "https://tiktok.com/@BeyondTourTravel", testid: "contact-tiktok" },
  ];

  return (
    <>
      <SEO
        title="Contact — Hubungi Beyond Travel | Land Arrangement Internasional"
        description="Hubungi Beyond Travel via WhatsApp, email, Instagram, atau TikTok. Kantor: Ruko U-Ville Avenue, Ciputat, Tangerang Selatan."
      />
      <PageHero
        image={HERO_IMG}
        eyebrow="Contact"
        title="Mari Terhubung"
        sub="Konsultasikan rencana perjalanan Anda — tim kami siap membantu kapan pun Anda siap."
        crumb="Contact"
      >
        <a href={waLink(settings, MSG.general)} target="_blank" rel="noreferrer" onClick={() => track("click_whatsapp", { source: "contact_hero" })} className="btn btn-wa !px-8 !py-3.5" data-testid="contact-hero-whatsapp-cta">
          <WhatsAppIcon className="h-5 w-5" /> Chat via WhatsApp
        </a>
      </PageHero>

      <section className="section bg-cream">
        <div className="container-x">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {cards.map((c, i) => (
              <Reveal key={c.label} delay={Math.min(i * 0.06, 0.3)} className="h-full">
                <a
                  href={c.href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-full flex-col gap-3 rounded-3xl bg-white p-5 shadow-crystal card-hover"
                  data-testid={c.testid}
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-tosca-600 text-gold">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{c.label}</span>
                  <span className="text-sm font-semibold text-charcoal">{c.value}</span>
                </a>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            <Reveal>
              <div className="rounded-3xl bg-white p-6 shadow-crystal sm:p-8" data-testid="contact-form">
                <SectionHeading eyebrow="Kirim Pesan" title="Atau Tinggalkan Pesan" />
                {sent ? (
                  <div className="mt-6 flex items-center gap-3 rounded-2xl bg-tosca-50 p-5 text-sm text-tosca-700" data-testid="contact-form-success">
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0" /> Pesan Anda terkirim. Tim kami akan menghubungi Anda segera.
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-6 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <input className="field" placeholder="Nama Anda" value={form.name} onChange={set("name")} data-testid="contact-form-name-input" />
                      <input className="field" placeholder="No. WhatsApp" value={form.whatsapp} onChange={set("whatsapp")} data-testid="contact-form-whatsapp-input" />
                    </div>
                    <textarea className="field min-h-28" placeholder="Ceritakan rencana perjalanan Anda..." value={form.message} onChange={set("message")} data-testid="contact-form-message-input" />
                    <button type="submit" className="btn btn-primary w-full" data-testid="contact-form-submit-button">
                      <Send className="h-4 w-4" /> Kirim Pesan
                    </button>
                  </form>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="h-full overflow-hidden rounded-3xl bg-white shadow-crystal" data-testid="contact-map">
                <div className="flex items-center gap-2 p-5">
                  <MapPin className="h-5 w-5 text-tosca-600" />
                  <p className="font-heading text-sm font-bold text-charcoal">Kantor Beyond Travel</p>
                </div>
                <p className="px-5 pb-4 text-sm leading-relaxed text-slate-500">
                  Gedung Datascrip Kav 9, lantai 6 Jl. Selaparang Blok B-15, RW.10, Gn. Sahari Sel., Kec. Kemayoran, Kota Jakarta Pusat, Daerah Khusus Ibukota Jakarta 10610
                </p>
                <iframe
                  title="Peta Kantor Beyond Travel"
                  src="https://www.google.com/maps?q=Ruko+U-Ville+Avenue+Jl.+Boulevard+UPJ+Ciputat+Tangerang+Selatan&output=embed"
                  className="h-72 w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
